import {
  HeadingStyle,
  DropStyle,
  WindowConfig,
  CurtainStyleConfig,
  FabricConfig,
  LiningConfig,
  HardwareConfig,
  CutCalculation,
  PanelCut,
  PurchaseOrder,
  PurchaseOrderItem,
  CutSheet,
  WindowOrderSummary,
  LiningType
} from '../types/curtain';

/**
 * Converts millimeters to meters.
 */
export const mmToMeters = (mm: number): number => mm / 1000;

/**
 * Converts meters to millimeters.
 */
export const metersToMm = (m: number): number => m * 1000;

/**
 * Returns the default fullness ratio for a given heading style.
 * S-Fold: 2.15, PinchPleat: 2.35, Eyelet: 1.9, PencilPleat: 2.0
 */
export function getDefaultFullnessRatio(headingStyle: HeadingStyle): number {
  switch (headingStyle) {
    case HeadingStyle.SFold: return 2.15;
    case HeadingStyle.PinchPleat: return 2.35;
    case HeadingStyle.Eyelet: return 1.9;
    case HeadingStyle.PencilPleat: return 2.0;
    default: return 2.0;
  }
}

/**
 * Returns the heading allowance in mm.
 * S-Fold: 80, PinchPleat: 150, Eyelet: 40, PencilPleat: 100
 */
export function getHeadingAllowance(headingStyle: HeadingStyle): number {
  switch (headingStyle) {
    case HeadingStyle.SFold: return 80;
    case HeadingStyle.PinchPleat: return 150;
    case HeadingStyle.Eyelet: return 40;
    case HeadingStyle.PencilPleat: return 100;
    default: return 100;
  }
}

/**
 * Returns the hem allowance in mm based on drop style.
 * Standard double hem: 200mm for floor-length, 150mm for sill-length
 */
export function getHemAllowance(dropStyle: DropStyle): number {
  return dropStyle === DropStyle.SillLength ? 150 : 200;
}

/**
 * Calculates the pattern adjustment needed to round up to the next full pattern repeat.
 * Formula: if verticalRepeat > 0: (Math.ceil(cutDrop / verticalRepeat) * verticalRepeat) - cutDrop, else 0
 */
export function calculatePatternAdjustment(cutDrop: number, verticalRepeat: number): number {
  if (verticalRepeat > 0) {
    const fullRepeats = Math.ceil(cutDrop / verticalRepeat);
    return (fullRepeats * verticalRepeat) - cutDrop;
  }
  return 0;
}

/**
 * Calculates the total fabric width required in mm.
 * Formula: (trackWidth * fullnessRatio) + (returnWidth * 2) + (isPair ? overlap * 2 : 0)
 */
export function calculateTotalFabricWidth(config: WindowConfig, styleConfig: CurtainStyleConfig): number {
  const fullnessRatio = styleConfig.fullnessRatio || getDefaultFullnessRatio(styleConfig.headingStyle);
  const baseWidth = config.trackWidth * fullnessRatio;
  const returnAllowance = styleConfig.returnWidth * 2;
  const overlapAllowance = styleConfig.isPair ? styleConfig.overlap * 2 : 0;
  
  return baseWidth + returnAllowance + overlapAllowance;
}

/**
 * Calculates the number of widths required.
 * Math.ceil(totalFabricWidth / usableFabricWidth)
 */
export function calculateNumberOfWidths(totalFabricWidth: number, usableFabricWidth: number): number {
  if (usableFabricWidth <= 0) return 0;
  return Math.ceil(totalFabricWidth / usableFabricWidth);
}

function getDropStyleAdjustment(dropStyle: DropStyle, floorClearance: number = 0): number {
  switch (dropStyle) {
    case DropStyle.Floating: return -floorClearance;
    case DropStyle.Touching: return 0;
    case DropStyle.Puddle: return floorClearance;
    case DropStyle.SillLength: return 0;
    default: return 0;
  }
}

/**
 * Calculates the cut drop per width in mm.
 * finishedDrop + headingAllowance + hemAllowance + patternAdjustment + dropStyleAdjustment
 */
export function calculateCutDrop(config: WindowConfig, styleConfig: CurtainStyleConfig, fabricConfig: FabricConfig): number {
  const headingAllowance = getHeadingAllowance(styleConfig.headingStyle);
  const hemAllowance = getHemAllowance(config.dropStyle);
  const dropStyleAdjustment = getDropStyleAdjustment(config.dropStyle, config.floorClearance ?? 0);
  
  const preliminaryDrop = config.finishedDrop + headingAllowance + hemAllowance + dropStyleAdjustment;
  const patternAdjustment = calculatePatternAdjustment(preliminaryDrop, fabricConfig.verticalRepeat);
  
  return preliminaryDrop + patternAdjustment;
}

/**
 * Generates the panel breakdown.
 */
export function calculatePanelBreakdown(
  numberOfWidths: number, 
  cutDrop: number, 
  fabricWidth: number, 
  _isPair: boolean, 
  verticalRepeat: number
): PanelCut[] {
  const panels: PanelCut[] = [];
  const seamAllowance = 15;
  for (let i = 1; i <= numberOfWidths; i++) {
    panels.push({
      panelNumber: i,
      cutDrop: cutDrop,
      finishedWidth: fabricWidth - (seamAllowance * 2),
      patternStartOffset: verticalRepeat > 0 ? ((i - 1) * cutDrop) % verticalRepeat : 0,
      seamAllowance,
      isHalfWidth: false,
    });
  }
  return panels;
}

/**
 * Calculates the full cut configuration.
 */
export function calculateFullCut(
  windowConfig: WindowConfig,
  styleConfig: CurtainStyleConfig,
  fabricConfig: FabricConfig,
  _liningConfig: LiningConfig
): CutCalculation {
  const totalFabricWidth = calculateTotalFabricWidth(windowConfig, styleConfig);
  const numberOfWidths = calculateNumberOfWidths(totalFabricWidth, fabricConfig.fabricWidth);
  const cutDrop = calculateCutDrop(windowConfig, styleConfig, fabricConfig);
  
  const headingAllowance = getHeadingAllowance(styleConfig.headingStyle);
  const hemAllowance = getHemAllowance(windowConfig.dropStyle);
  const dropStyleAdjustment = getDropStyleAdjustment(windowConfig.dropStyle, windowConfig.floorClearance ?? 0);
  const preliminaryDrop = windowConfig.finishedDrop + headingAllowance + hemAllowance + dropStyleAdjustment;
  const patternAdjustment = calculatePatternAdjustment(preliminaryDrop, fabricConfig.verticalRepeat);
  
  const totalFabricLength = cutDrop * numberOfWidths;
  const totalFabricMeters = mmToMeters(totalFabricLength);
  
  const pureArea = totalFabricWidth * windowConfig.finishedDrop;
  const totalArea = numberOfWidths * fabricConfig.fabricWidth * cutDrop;
  const wastagePercent = totalArea > 0 ? Math.max(0, ((totalArea - pureArea) / totalArea) * 100) : 0;
  
  const liningCutDrop = windowConfig.finishedDrop + getHemAllowance(windowConfig.dropStyle);
  const liningMeters = mmToMeters(liningCutDrop * numberOfWidths);

  const panelBreakdown = calculatePanelBreakdown(
    numberOfWidths, 
    cutDrop, 
    fabricConfig.fabricWidth, 
    styleConfig.isPair, 
    fabricConfig.verticalRepeat
  );

  return {
    totalFabricWidth,
    numberOfWidths,
    cutDrop,
    patternAdjustment,
    headingAllowance,
    hemAllowance,
    totalFabricLength,
    totalFabricMeters,
    wastagePercent,
    liningMeters,
    panelBreakdown
  };
}

/**
 * Calculates the total price for a single window.
 */
export function calculatePrice(
  calculation: CutCalculation,
  fabricConfig: FabricConfig,
  liningConfig: LiningConfig,
  hardwareConfig: HardwareConfig
): number {
  const fabricCost = calculation.totalFabricMeters * fabricConfig.pricePerMeter;
  const liningCost = liningConfig.liningType !== LiningType.None ? calculation.liningMeters * liningConfig.pricePerMeter : 0;
  const hardwareCost = hardwareConfig.pricePerMeter + hardwareConfig.accessoriesPrice; // Simplistic
  return fabricCost + liningCost + hardwareCost;
}

/**
 * Generates a purchase order.
 */
export function generatePurchaseOrder(orders: WindowOrderSummary[], customerName: string): PurchaseOrder {
  let totalFabricCost = 0;
  let totalLiningCost = 0;
  let totalHardwareCost = 0;
  
  const items: PurchaseOrderItem[] = orders.map(order => {
    const fabricCost = order.calculation.totalFabricMeters * order.fabricConfig.pricePerMeter;
    const liningCost = order.liningConfig.liningType !== LiningType.None 
      ? order.calculation.liningMeters * order.liningConfig.pricePerMeter 
      : 0;
    const hwCost = order.hardwareConfig.pricePerMeter + order.hardwareConfig.accessoriesPrice;
    
    totalFabricCost += fabricCost;
    totalLiningCost += liningCost;
    totalHardwareCost += hwCost;
    
    return {
      windowRef: `${order.windowConfig.roomName} - ${order.windowConfig.windowName}`,
      fabricCode: order.fabricConfig.fabricCode,
      metersRequired: order.calculation.totalFabricMeters,
      liningMeters: order.calculation.liningMeters,
      hardwareDescription: order.hardwareConfig.trackCode
    };
  });
  
  return {
    orderNumber: `PO-${Date.now()}`,
    date: new Date().toISOString(),
    customerName,
    items,
    totalFabricCost,
    totalLiningCost,
    totalHardwareCost,
    grandTotal: totalFabricCost + totalLiningCost + totalHardwareCost
  };
}

/**
 * Generates a cut sheet.
 */
export function generateCutSheet(order: WindowOrderSummary): CutSheet {
  return {
    orderNumber: `PO-${Date.now()}`,
    windowRef: `${order.windowConfig.roomName} - ${order.windowConfig.windowName}`,
    fabricCode: order.fabricConfig.fabricCode,
    fabricWidth: order.fabricConfig.fabricWidth,
    headingStyle: order.styleConfig.headingStyle,
    mountType: order.windowConfig.mountType,
    trackWidth: order.windowConfig.trackWidth,
    finishedDrop: order.windowConfig.finishedDrop,
    panels: order.calculation.panelBreakdown,
    headingPrep: `Prepare for ${order.styleConfig.headingStyle}`,
    hemDepth: getHemAllowance(order.windowConfig.dropStyle),
    notes: []
  };
}
